import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useAiChatContext } from "src/_modules/AiSupportChat/context/AiChatProvider";
import { OPENING_STATUS } from "src/_modules/AiSupportChat/hooks/useAiChat";
import MessageList from "src/_modules/AiSupportChat/components/MessageList";
import ReadOnlyFooter from "src/_modules/AiSupportChat/components/ReadOnlyFooter";
import Composer from "src/_modules/AiSupportChat/components/Composer";
import InlineNotice from "src/_modules/AiSupportChat/components/InlineNotice";
import { RETENTION_FALLBACK_DAYS } from "src/_modules/AiSupportChat/constants";
import {
  CHAT_PHASES,
  NOTICE_KINDS,
  SEND_BLOCKERS,
  getSendBlocker,
} from "src/_modules/AiSupportChat/core/chatReducer";
import { findQuestion } from "src/_modules/AiSupportChat/core/messageText";
import {
  CONVERSATION_STATUS,
  MESSAGE_ROLES,
  MESSAGE_STATUS,
} from "src/_modules/AiSupportChat/core/protocol";

/** Skeleton bubbles while a conversation loads: [side, width]. */
const SKELETON_BUBBLES = [
  ["flex-end", "55%"],
  ["flex-start", "80%"],
  ["flex-end", "40%"],
  ["flex-start", "70%"],
];

/** Composer states that block Send but keep the field editable. */
const BLOCKS_SEND = [SEND_BLOCKERS.COOLDOWN, SEND_BLOCKERS.UNAVAILABLE];

/**
 * Notices shown above the messages of an open conversation (plan §3.7).
 * `tooLong` is the composer's (D69), `notFound` shows on home (D55).
 */
const VIEW_NOTICES = [
  NOTICE_KINDS.RATE_LIMITED,
  NOTICE_KINDS.UNAVAILABLE,
  NOTICE_KINDS.IN_PROGRESS,
  NOTICE_KINDS.NETWORK,
  NOTICE_KINDS.GENERIC,
];

/**
 * The answer a `network` notice's Retry re-sends: the last message, when it
 * is an answer that failed and may be retried (as its own Retry), else null.
 */
const retryableAnswerId = (messages) => {
  const last = messages[messages.length - 1];
  const failed =
    last?.role === MESSAGE_ROLES.ASSISTANT &&
    (last.status === MESSAGE_STATUS.FAILED ||
      last.status === MESSAGE_STATUS.INTERRUPTED) &&
    last.error?.retryable !== false;
  return failed ? last.id : null;
};

/**
 * Re-renders once `until` (epoch ms) has passed, so a state computed from
 * `Date.now()` (the 853 cooldown) ends on time. Checks again if a timer
 * fires early (clock coarsening) and never fires after unmount.
 *
 * @param {number | null} until
 */
const useRenderAfter = (until) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (until === null) return undefined;
    let timer;
    const check = () => {
      const left = until - Date.now();
      if (left > 0) timer = setTimeout(check, left + 20);
      else setTick((tick) => tick + 1);
    };
    check();
    return () => clearTimeout(timer);
  }, [until]);
};

/** The AI disclosure (non-negotiable #2) opens every conversation. */
const Disclosure = ({ days }) => {
  const { t } = useTranslation("aiSupport");
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, pb: 0.5 }}>
      <InfoOutlined
        fontSize="small"
        color="action"
        aria-hidden="true"
        sx={{ mt: 0.25 }}
      />
      <Typography variant="caption" color="text.secondary">
        {t("disclosure", { days })}
      </Typography>
    </Box>
  );
};

/**
 * Takes focus while loading: the row (or Retry) that opened the view is
 * gone, and focus on <body> wouldn't reach the panel's Esc (D37). The
 * composer / footer autoFocus takes over once the conversation is in.
 */
const LoadingState = () => {
  const { t } = useTranslation("aiSupport");
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    // Focused while the conversation opens: a name, so it isn't read as
    // nothing
    <Box
      ref={ref}
      tabIndex={-1}
      role="group"
      aria-label={t("conversation.loading")}
      aria-busy="true"
      sx={{ flex: 1, p: 2, outline: "none" }}
    >
      <Stack spacing={1.5} aria-hidden="true">
        {SKELETON_BUBBLES.map(([side, width], index) => (
          <Box key={index} sx={{ display: "flex", justifyContent: side }}>
            <Skeleton
              variant="rounded"
              width={width}
              height={index % 2 ? 64 : 36}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const LoadError = ({ onRetry }) => {
  const { t } = useTranslation("aiSupport");
  return (
    <Box sx={{ flex: 1, p: 2 }}>
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button
            autoFocus
            color="inherit"
            size="small"
            onClick={onRetry}
            sx={{ textTransform: "none" }}
          >
            {t("message.retry")}
          </Button>
        }
        sx={{ alignItems: "center" }}
      >
        {t("errors.loadHistory")}
      </Alert>
    </Box>
  );
};

/**
 * One conversation (plan §3.6): the message list, then the composer (open
 * conversation or draft) or the read-only footer (closed). Keyed by
 * conversation id in `AiChatPanel` (a draft is `'draft'`), so every
 * conversation gets a fresh scroll position and focus.
 *
 * A saved conversation that isn't in the chat state yet (a row click, a
 * view restored from sessionStorage) is loaded on mount: skeleton, then the
 * messages, or an error with Retry. A 855 lands home (provider signal).
 *
 * The draft view becomes the conversation's view once `meta` names it
 * (AiChatProvider), which remounts this view mid-answer: the stream lives
 * in the chat state, so only scroll position and focus start again.
 *
 * @param {object} props
 * @param {string | null} props.conversationId null for the draft
 * @param {(prefill?: string) => void} props.onStartNew opens a new draft (stable)
 */
export default function ConversationView({ conversationId, onStartNew }) {
  const { t } = useTranslation("aiSupport");
  const { chat, eligibility, debugOn } = useAiChatContext();
  const {
    state,
    opening,
    ensureConversation,
    openConversation,
    send,
    stop,
    retry,
    markFeedbackSaved,
    dismissNotice,
    clearRestoreText,
  } = chat;
  const { conversation, messages } = state;
  const isDraftView = conversationId === null;
  const busy = state.phase !== CHAT_PHASES.IDLE;
  // A draft view shows the draft in the chat state, even once `meta` gives
  // it an id; a saved conversation only once it is the one loaded
  const loaded =
    isDraftView ||
    (conversation !== null &&
      !conversation.isDraft &&
      conversation.id === conversationId);
  const closed = loaded && conversation?.status === CONVERSATION_STATUS.CLOSED;
  useRenderAfter(state.cooldownUntil);

  // Re-checked when an answer ends: opening is refused mid-answer
  useEffect(() => {
    if (!isDraftView) ensureConversation(conversationId);
  }, [isDraftView, conversationId, ensureConversation, busy]);

  // Retry reads the latest messages without changing identity (memoized bubbles)
  const messagesRef = useRef(messages);
  useLayoutEffect(() => {
    messagesRef.current = messages;
  });

  // A closed conversation can't take the question again: Retry starts a
  // new draft with it. In an open one, Retry re-sends it in place.
  const retryInNewDraft = useCallback(
    (messageId) => onStartNew(findQuestion(messagesRef.current, messageId)),
    [onStartNew]
  );

  if (!loaded) {
    const failed =
      opening.id === conversationId && opening.status === OPENING_STATUS.FAILED;
    return failed ? (
      <LoadError onRetry={() => openConversation(conversationId)} />
    ) : (
      <LoadingState />
    );
  }

  const retentionDays =
    eligibility.data?.retentionDays ?? RETENTION_FALLBACK_DAYS;
  // A read-only conversation can't send: the cooldown / 852 don't apply
  const notice =
    !closed && VIEW_NOTICES.includes(state.notice?.kind) ? state.notice : null;
  const retryId =
    notice?.kind === NOTICE_KINDS.NETWORK ? retryableAnswerId(messages) : null;

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <InlineNotice
        notice={notice}
        onDismiss={dismissNotice}
        onRetry={retryId ? () => retry(retryId) : undefined}
        whatsappUrl={eligibility.data?.support?.whatsappUrl}
      />
      <MessageList
        messages={messages}
        onRetry={closed ? retryInNewDraft : retry}
        onFeedbackSaved={markFeedbackSaved}
        // `debugOn` is only ever true for AI developers
        showDebug={debugOn}
        label={conversation?.title || t("conversation.newTitle")}
        intro={<Disclosure days={retentionDays} />}
      />
      {closed ? (
        <ReadOnlyFooter onStartNew={onStartNew} />
      ) : (
        <Composer
          busy={busy}
          sendBlocked={BLOCKS_SEND.includes(getSendBlocker(state, Date.now()))}
          maxChars={eligibility.data?.maxMessageChars}
          // 857: the limit was stale; eligibility is being refetched
          tooLongNotice={state.notice?.kind === NOTICE_KINDS.TOO_LONG}
          onTooLongEdited={dismissNotice}
          onSend={send}
          onStop={stop}
          restoreText={state.restoreText}
          onRestoreTaken={clearRestoreText}
        />
      )}
    </Box>
  );
}
